import React, { useRef, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { createRoot, Root } from 'react-dom/client';
import classNames from 'classnames';
import flatpickr from 'flatpickr';
import { HiOutlineArrowNarrowLeft, HiOutlineArrowNarrowRight } from 'react-icons/hi';
import { Mandarin } from "flatpickr/dist/l10n/zh.js";
import { Indonesian } from "flatpickr/dist/l10n/id.js";
import { Malaysian } from "flatpickr/dist/l10n/ms.js";
import { German } from "flatpickr/dist/l10n/de.js";
import { Dutch } from "flatpickr/dist/l10n/nl.js";
import { useLocale } from 'next-intl';

type Props = {
  wrapperClassName?: string;
  minDate?: Date;
  maxDate?: Date;
  inline?: boolean;
  mode: 'single' | 'range';
  onChange?: (dates: Date[]) => void;
  flatpickrRef?: React.MutableRefObject<flatpickr.Instance | undefined>;
  showMonthYearSelector: boolean;
};

export function getFlatpickrLocale(locale: string) {
  const map: Record<string, any> = {
    en: undefined,
    zh: Mandarin,
    id: Indonesian,
    ms: Malaysian,
    de: German,
    nl: Dutch,
  };

  return map[locale];
}

const YEARS_PER_PAGE = 15;

type MonthYearSelectorProps = {
  fp: flatpickr.Instance;
  month: number;
  year: number;
};

const MonthYearSelector: React.FC<MonthYearSelectorProps> = ({ fp, month, year }) => {
  const [view, setView] = useState<'days' | 'months' | 'years'>('days');
  const [yearStart, setYearStart] = useState(0);
  const monthRef = useRef<HTMLButtonElement>(null);
  const yearRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const picking = view !== 'days';
    fp.rContainer?.classList.toggle('tw-hidden', picking);
    fp.prevMonthNav?.classList.toggle('tw-invisible', picking);
    fp.nextMonthNav?.classList.toggle('tw-invisible', picking);
  }, [fp, view]);

  if (!fp.config) return null;

  const { minDate, maxDate } = fp.config;

  const yearDisabled = (y: number) =>
    (!!minDate && y < minDate.getFullYear()) || (!!maxDate && y > maxDate.getFullYear());
  const pageLabel = (start: number) => `${start} – ${start + YEARS_PER_PAGE - 1}`;
  const monthDisabled = (m: number) =>
    (!!minDate && new Date(year, m + 1, 1) <= minDate) ||
    (!!maxDate && new Date(year, m, 1) > maxDate);

  const toggle = (next: 'months' | 'years') => {
    // Open the year grid with the current year in the middle.
    if (next === 'years') setYearStart(year - Math.floor(YEARS_PER_PAGE / 2));
    setView(view === next ? 'days' : next);
  };

  const jump = (y: number, m: number) => {
    if (minDate && y === minDate.getFullYear()) m = Math.max(m, minDate.getMonth());
    if (maxDate && y === maxDate.getFullYear()) m = Math.min(m, maxDate.getMonth());
    (view === 'years' ? yearRef : monthRef).current?.focus();
    setView('days');
    if (y === year && m === month) return;
    fp.jumpToDate(new Date(y, m, 1), true);
  };

  const prevPageDisabled = yearDisabled(yearStart - 1);
  const nextPageDisabled = yearDisabled(yearStart + YEARS_PER_PAGE);

  const item = (
    label: string,
    selected: boolean,
    disabled: boolean,
    onClick: () => void,
    ariaLabel?: string
  ) => (
    <button
      key={label}
      type="button"
      className={classNames(
        'tw-justify-self-center tw-w-11 tw-h-11 tw-p-0 tw-bg-transparent tw-border tw-border-solid tw-rounded-lg',
        selected ? 'tw-border-brand-navy tw-font-semibold' : 'tw-border-transparent',
        disabled
          ? 'tw-cursor-not-allowed tw-text-grey-lighter'
          : 'tw-text-brand-navy hover:tw-border-brand-navy'
      )}
      aria-label={ariaLabel}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
    >
      {label}
    </button>
  );

  return (
    // flatpickr's <body> keydown handler swallows Enter and sends arrow keys to the hidden days.
    <div
      onKeyDown={(e) =>
        (e.key === 'Enter' || (view !== 'days' && e.key.startsWith('Arrow'))) &&
        e.stopPropagation()
      }
    >
      {/* Year paging arrows reuse flatpickr's arrow classes; tw-top-1 offsets its 4px padding. */}
      {view === 'years' && (
        <button
          type="button"
          className={classNames('flatpickr-prev-month tw-top-1 tw-border-0 tw-bg-transparent', {
            'flatpickr-disabled': prevPageDisabled,
          })}
          aria-label={pageLabel(yearStart - YEARS_PER_PAGE)}
          aria-disabled={prevPageDisabled}
          onClick={() => !prevPageDisabled && setYearStart(yearStart - YEARS_PER_PAGE)}
        >
          <HiOutlineArrowNarrowLeft strokeWidth={2} />
        </button>
      )}
      <div className="flatpickr-current-month">
        <button
          ref={monthRef}
          type="button"
          className="tw-px-0.5 tw-border-0 tw-rounded-sm tw-bg-transparent tw-text-brand-navy tw-text-base tw-leading-none tw-font-semibold hover:tw-bg-brand-yellow"
          aria-label={`${fp.l10n.monthAriaLabel} ${fp.l10n.months.longhand[month]}`}
          aria-expanded={view === 'months'}
          onClick={() => toggle('months')}
        >
          {fp.l10n.months.longhand[month]}
        </button>{' '}
        <button
          ref={yearRef}
          type="button"
          className="tw-px-0.5 tw-border-0 tw-rounded-sm tw-bg-transparent tw-text-brand-navy tw-text-base tw-leading-none tw-font-semibold hover:tw-bg-brand-yellow"
          aria-label={`${fp.l10n.yearAriaLabel} ${year}`}
          aria-expanded={view === 'years'}
          onClick={() => toggle('years')}
        >
          {year}
        </button>
      </div>
      {view === 'years' && (
        <button
          type="button"
          className={classNames('flatpickr-next-month tw-top-1 tw-border-0 tw-bg-transparent', {
            'flatpickr-disabled': nextPageDisabled,
          })}
          aria-label={pageLabel(yearStart + YEARS_PER_PAGE)}
          aria-disabled={nextPageDisabled}
          onClick={() => !nextPageDisabled && setYearStart(yearStart + YEARS_PER_PAGE)}
        >
          <HiOutlineArrowNarrowRight strokeWidth={2} />
        </button>
      )}
      {view !== 'days' &&
        createPortal(
          <div
            className={classNames(
              'tw-grid tw-w-full tw-gap-y-3 tw-pt-4 tw-pb-2',
              view === 'months' ? 'tw-grid-cols-4' : 'tw-grid-cols-5'
            )}
          >
            {view === 'months'
              ? fp.l10n.months.shorthand.map((label, m) =>
                  item(
                    label,
                    m === month,
                    monthDisabled(m),
                    () => jump(year, m),
                    fp.l10n.months.longhand[m]
                  )
                )
              : Array.from({ length: YEARS_PER_PAGE }, (_, i) => yearStart + i).map((y) =>
                  item(String(y), y === year, yearDisabled(y), () => jump(y, month))
                )}
          </div>,
          fp.innerContainer as HTMLElement
        )}
    </div>
  );
};

const DatePicker: React.FC<Props> = ({
  wrapperClassName,
  minDate = new Date(),
  maxDate,
  inline,
  mode,
  onChange,
  flatpickrRef,
  showMonthYearSelector
}) => {
  const datepickerEleRef = useRef<HTMLDivElement>(null);
  const internalRef = useRef<flatpickr.Instance>();
  const instanceRef = flatpickrRef || internalRef;
  const locale = useLocale()

  useEffect(() => {
    if (datepickerEleRef.current) {
      let monthYearRoot: Root | undefined;
      const renderMonthYearSelector = (instance: flatpickr.Instance) =>
        monthYearRoot?.render(
          <MonthYearSelector fp={instance} month={instance.currentMonth} year={instance.currentYear} />
        );

      instanceRef.current = flatpickr(datepickerEleRef.current, {
        locale: getFlatpickrLocale(locale),
        mode: mode,
        inline: inline,
        static: true,
        minDate: minDate,
        maxDate: maxDate,
        prevArrow: '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18"></path></svg>',
        nextArrow: '<svg stroke="currentColor" fill="none" stroke-width="2" viewBox="0 0 24 24" aria-hidden="true" height="1em" width="1em" xmlns="http://www.w3.org/2000/svg"><path stroke-linecap="round" stroke-linejoin="round" d="M17 8l4 4m0 0l-4 4m4-4H3"></path></svg>',
        dateFormat: 'd-m-Y',
        onReady: function (_, __, instance) {
          if (!showMonthYearSelector) {
            const selectEle: HTMLSelectElement | null = instance.calendarContainer.querySelector(
              '.flatpickr-monthDropdown-months'
            );
            if (selectEle) selectEle.disabled = true;
            const inputEle: HTMLInputElement | null =
              instance.calendarContainer.querySelector('.numInput');
            if (inputEle) inputEle.disabled = true;
            const arrowUpEle: HTMLElement | null =
              instance.calendarContainer.querySelector('.arrowUp');
            if (arrowUpEle) arrowUpEle.remove();
            const arrowDownEle: HTMLElement | null =
              instance.calendarContainer.querySelector('.arrowDown');
            if (arrowDownEle) arrowDownEle.remove();
          } else {
            const monthEle = instance.calendarContainer.querySelector('.flatpickr-month');
            if (!monthEle) return;
            monthEle.textContent = '';
            monthYearRoot = createRoot(monthEle);
            renderMonthYearSelector(instance);
          }
        },
        onMonthChange: function (_, __, instance) {
          renderMonthYearSelector(instance);
        },
        onYearChange: function (_, __, instance) {
          renderMonthYearSelector(instance);
        },
        onChange: function (dateObj) {
          typeof onChange === 'function' && onChange(dateObj);
          // const displayDates = dateObj.map(function (date) {
          //   return flatpickr.formatDate(date, 'M d');
          // });
          // setDisplayDate(displayDates.join(' - '));
          // const strDates = dateObj.map(function (date) {
          //   return flatpickr.formatDate(date, 'Y-m-d');
          // })
          // Cookies.set('start_date', strDates.join(','), { path: '/' });
        },
      });
      // Unmounting a root synchronously inside a React effect cleanup isn't allowed.
      return () => {
        setTimeout(() => monthYearRoot?.unmount());
      };
    }
  }, []);

  return (
    <div className={`flatpickr-brand ${showMonthYearSelector ? 'flatpickr-with-selector' : ''} ${wrapperClassName || ''}`}>
      <div ref={datepickerEleRef}></div>
    </div>
  );
};

export default DatePicker;
